import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector, useLogger, useI18n } from '~/Hooks';
import _ from 'lodash';
import { useStorage } from '~Components/Storage';
import QuestionModal from '~ComponentsImproved/QuestionModal';
import usePrintReceipt from '~Hooks/usePrintReceipt';
import useModal from '~ComponentsImproved/Modal/useModal';

const MAX_PRINTED_IDS = 10;

interface Props {
  venueId: string;
}

const ReceiptPrinter: FC<Props> = ({ venueId }) => {
  const print = useSelector((state) => state.POSReceiptPrints);
  const POSOrder = useSelector((state) => print && state.POSOrders.find((o) => o.id === print.id));
  const { printReceipt } = usePrintReceipt({ venueId });
  const [storageData] = useStorage();

  const askToPrintModal = useModal();

  // Set to true when the user confirms the print (yes/no)
  const [printConfirmed, setPrintConfirmed] = useState(!storageData.askToPrint);
  const printedIdsRef = useRef<string[]>([]);

  useEffect(() => setPrintConfirmed(!storageData.askToPrint), [storageData.askToPrint]);

  const { i18n } = useI18n();

  const logger = useLogger('receipt-printer');

  // If we sync the order before printing, POSPayment and POSOrder will be null, so we need to use the print state.
  const order = POSOrder || print?.order;

  useEffect(() => {
    if (location.pathname.includes('settings')) {
      return;
    }

    if (!order) {
      return;
    }

    if (!print || !!order?.noReceipt) {
      logger.info('ReceiptPrinter: No print data found');
      return;
    }

    // If we've already printed this ID, close modal and return
    if (print?.id && printedIdsRef.current.includes(print.id)) {
      logger.info('ReceiptPrinter: Already printed this order, closing modal');
      askToPrintModal.hide();
      return;
    }

    // We skip printing if manualReceiptPrint is true
    if (storageData.manualReceiptPrint) {
      logger.info('ReceiptPrinter: Skipping print due to manualReceiptPrint');
      return;
    }

    // If storageData.askToPrint is true, we prompt the user to print
    if (!printConfirmed) {
      askToPrintModal.toggle();
      return;
    }

    // Set the confirmed state back to false if we should prompt on every print
    storageData.askToPrint && setPrintConfirmed(false);

    if (!print || !!POSOrder?.noReceipt || storageData.manualReceiptPrint) {
      return;
    }

    printReceipt();

    printedIdsRef.current = _.compact([print?.id, ...printedIdsRef.current]).slice(
      0,
      MAX_PRINTED_IDS,
    );
  }, [print, order, printConfirmed]);

  const handleNoPrintClick = () => {
    logger.info('ReceiptPrinter: User declined to print');
    setPrintConfirmed(false);
    askToPrintModal.hide();
  };

  const handlePrintClick = () => {
    logger.info('ReceiptPrinter: User clicked yes to print');
    setPrintConfirmed(true);
    askToPrintModal.hide();
  };

  return (
    <QuestionModal
      question={i18n('TM.WantToPrint')}
      isShowing={askToPrintModal.isShowing && !location.pathname.includes('settings')}
      width="auto"
      onLeftClick={handleNoPrintClick}
      onRightClick={handlePrintClick}
      backdropClose={askToPrintModal.hide}
    />
  );
};

export default ReceiptPrinter;
